import React, { useContext, useEffect, useRef, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { FEATURE_FLAGS } from '../../config/featureFlags';
import MapyCzContext from '../../context/MapyCzContext';
import { GameMap } from '../LeafletMap';
import { setCurrentMapLayer } from '../../redux/actions/game';
import { mapLayers } from '../../redux/reducers/game';
import {
    drawSingleRoundResultLayerToMap,
    getMapInstanceByGameMode,
    setupMapInstance,
    setupMarkerLayerOnly,
} from '../../util/map';

/**
 * OPTIMALIZACE: SMap komponenta
 * - Pro round mapu: instance se znovupoužívá mezi koly (jen reset středu a markerů)
 * - Pro result mapu: standardní vytvoření (je to jiný DOM element)
 * - Použita optimalizovaná marker vrstva bez POI
 */
const SMap = ({
    currentRoundGuessedPoint,
    type,
    onMapClick,
    refLayeredMapValue,
    refLayerValue,
    refVectorLayerSMapValue,
    isBattle,
}) => {
    const map = useRef();
    const dispatch = useDispatch();
    const mapyContext = useContext(MapyCzContext);

    const [mapLoaded, setMapLoaded] = useState(false);
    const [instance, setInstance] = useState(null);
    const [markerLayer, setMarkerLayer] = useState(null);
    const [vectorLayer, setVectorLayer] = useState(null);

    // Ref pro sledování předchozího kola (pro detekci změny) - jen pro round mapu
    const prevRoundRef = useRef(null);
    // Ref pro cleanup signal listeners
    const signalsRef = useRef(null);
    const onMapClickRef = useRef(onMapClick);

    const currentGame = useSelector(state => state.game.currentGame);
    const currentBattleInfo = useSelector(state => state.battle.currentBattle);

    const { mode: battleMode, radius: battleRadius, rounds, round } = currentBattleInfo;
    const { mode, city, radius, currentMapLayer } = currentGame;

    const isMapMetaInfoLoaded = isBattle ? battleMode && round && rounds : mode && radius;

    // Aktualizovat ref při změně callback
    onMapClickRef.current = onMapClick;

    const changeMapLayerListener = useCallback((event) => {
        if (event.target.constructor.NAME === 'SMap.Layer.Turist') {
            dispatch(setCurrentMapLayer(mapLayers.tourist));
        } else {
            dispatch(setCurrentMapLayer(mapLayers.default));
        }
    }, [dispatch]);

    /**
     * Získá město pro aktuální kolo
     */
    const getCurrentCity = useCallback(() => {
        if (isBattle && rounds && round) {
            return rounds[round - 1]?.city;
        }
        return city;
    }, [isBattle, rounds, round, city]);

    /**
     * Inicializace mapy
     */
    useEffect(() => {
        if (mapyContext.loadedMapApi && mapyContext.SMap && !mapLoaded && isMapMetaInfoLoaded && map.current) {
            const { SMap: SMapContext } = mapyContext;
            const currentCity = getCurrentCity();

            const mapInstanceInitObject = isBattle
                ? { mapMode: battleMode, mapCity: currentCity, mapRadius: battleRadius }
                : { mapMode: mode, mapCity: currentCity, mapRadius: radius };

            // Vytvoříme instanci mapy
            const mapInstance = getMapInstanceByGameMode(
                SMapContext,
                mapInstanceInitObject.mapMode,
                mapInstanceInitObject.mapCity,
                mapInstanceInitObject.mapRadius,
                map.current,
            );

            // Setup mapy
            setupMapInstance(SMapContext, mapInstance, currentMapLayer, false);

            // OPTIMALIZACE: Vytvoříme marker vrstvu bez POI (šetří requesty)
            const newMarkerLayer = setupMarkerLayerOnly(SMapContext, mapInstance);
            setMarkerLayer(newMarkerLayer);

            if (type === 'result') {
                drawSingleRoundResultLayerToMap(SMapContext, mapInstance, newMarkerLayer, currentRoundGuessedPoint);
            } else {
                // assign vrstva se značkami
                refLayerValue.current = newMarkerLayer;
                // assign layered map value
                refLayeredMapValue.current = mapInstance;
                const signals = refLayeredMapValue.current.getSignals();
                signalsRef.current = signals;

                signals.addListener(window, 'map-click', onMapClickRef.current);

                // vykreslit vektor do mapy
                const newVectorLayer = new SMapContext.Layer.Geometry();
                refLayeredMapValue.current.addLayer(newVectorLayer);
                newVectorLayer.enable();
                refVectorLayerSMapValue.current = newVectorLayer;
                setVectorLayer(newVectorLayer);

                signals.addListener(window, 'layer-enable', changeMapLayerListener);
            }

            setInstance(mapInstance);
            setMapLoaded(true);
            prevRoundRef.current = round;
        }
    }, [mapyContext.loadedMapApi, mapLoaded, isMapMetaInfoLoaded, currentMapLayer]);

    /**
     * OPTIMALIZACE: Při změně kola (jen pro round mapu) resetujeme střed a markery
     * Místo vytváření nové instance mapy
     */
    useEffect(() => {
        if (type === 'round' && instance && mapLoaded && round !== prevRoundRef.current) {
            const { SMap: SMapContext } = mapyContext;
            const currentCity = getCurrentCity();

            if (currentCity && currentCity.coordinates) {
                // Přesuneme střed mapy na nové město
                const newCenter = SMapContext.Coords.fromWGS84(
                    currentCity.coordinates.longitude,
                    currentCity.coordinates.latitude
                );

                // Nastavíme nový střed s animací
                instance.setCenterZoom(newCenter, instance.getZoom(), true);
            }

            // Vyčistíme marker vrstvu (markery z předchozího kola)
            if (markerLayer) {
                markerLayer.removeAll();
            }

            // Vyčistíme vector vrstvu (čáry z předchozího kola)
            if (vectorLayer) {
                vectorLayer.removeAll();
            }

            prevRoundRef.current = round;
        }
    }, [round, instance, mapLoaded, type, markerLayer, vectorLayer, mapyContext, getCurrentCity]);

    /**
     * Speciální případ pro battle - reload stránky s existujícím tipem
     */
    useEffect(() => {
        if (isBattle && mapyContext.loadedMapApi && mapyContext.SMap && instance && markerLayer) {
            const currentRound = rounds?.[round - 1];

            if (currentRound) {
                const { isGuessed, isRoundActive } = currentRound;

                if (isGuessed && !isRoundActive && currentRoundGuessedPoint) {
                    const { SMap: SMapContext } = mapyContext;
                    drawSingleRoundResultLayerToMap(SMapContext, instance, markerLayer, currentRoundGuessedPoint);
                }
            }
        }
    }, [mapyContext.loadedMapApi, instance, currentRoundGuessedPoint, markerLayer]);

    /**
     * OPTIMALIZACE: Cleanup signal listeners při unmount
     * Zabraňuje memory leakům a zbytečným event handlerům
     */
    useEffect(() => {
        const currentOnMapClick = onMapClickRef.current;
        const currentChangeMapLayerListener = changeMapLayerListener;

        return () => {
            if (signalsRef.current) {
                try {
                    signalsRef.current.removeListener(window, 'map-click', currentOnMapClick);
                    signalsRef.current.removeListener(window, 'layer-enable', currentChangeMapLayerListener);
                } catch (e) {
                    // Ignorovat chyby při cleanup (instance už může být zničena)
                }
            }
        };
    }, [changeMapLayerListener]);

    // Pro nové Leaflet API - handler pro kliknutí
    const handleLeafletMapClick = useCallback((coords) => {
        // Konverze z Leaflet formátu na SMap formát pro kompatibilitu
        if (onMapClick) {
            // Simulace SMap event objektu
            const fakeEvent = {
                data: {
                    event: {
                        target: null,
                    },
                },
            };
            // Uložíme souřadnice do ref pro použití v onMapClick handleru
            onMapClickRef.current?.(fakeEvent, coords);
        }
    }, [onMapClick]);

    // Výpočet středu pro Leaflet mapu
    const getLeafletCenter = useCallback(() => {
        const currentCity = getCurrentCity();
        if (currentCity?.coordinates) {
            return [
                currentCity.coordinates.latitude,
                currentCity.coordinates.longitude,
            ];
        }
        return [50.027429, 15.202828]; // Střed ČR
    }, [getCurrentCity]);

    // Výpočet optimálního zoomu podle herního módu a radiusu
    const getLeafletZoom = useCallback(() => {
        const gameMode = isBattle ? battleMode : mode;
        const gameRadius = isBattle ? battleRadius : radius;

        // Pro náhodný mód v rámci celé ČR
        if (gameMode === 'random') {
            return 7;
        }

        // Pro krajový mód
        if (gameMode === 'randomRegion') {
            return 8;
        }

        // Pro městský/custom mód - zoom podle radiusu
        if (gameRadius) {
            if (gameRadius <= 2) return 12;
            if (gameRadius <= 4) return 11;
            if (gameRadius <= 6) return 10;
            if (gameRadius <= 8) return 9;
            return 8;
        }

        return 7;
    }, [isBattle, battleMode, mode, battleRadius, radius]);

    // Pokud je zapnutý nový map API, použijeme Leaflet
    if (FEATURE_FLAGS.USE_NEW_MAP_API) {
        const center = getLeafletCenter();
        const zoom = getLeafletZoom();

        return (
            <GameMap
                center={center}
                zoom={zoom}
                onMapClick={handleLeafletMapClick}
                currentMapLayer={currentMapLayer}
                onLayerChange={(layer) => dispatch(setCurrentMapLayer(layer))}
                currentRoundGuessedPoint={type === 'result' ? currentRoundGuessedPoint : null}
            />
        );
    }

    return (
        <>
            <div id="smap" className="smap smap-style" ref={map} />
        </>
    );
};

export default SMap;
